import { ColumnType } from 'antd/lib/table';
import Table from 'antd/lib/table/Table';
import * as React from 'react';
import { AppComponentProps, AppRouteComponentMatchParams, OrgSettingsTabType } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../reducers/reducers';
import {
    selectAppName,
    selectOrgUsers,
    selectAppPlatform,
    selectAppStoreType,
    selectOrgGroups,
} from '../../selectors/selectors';
import { OrgUser, UserGroupId } from '../../types';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import { AppSettingsV2Updatable } from '../../actions/updatable';
import styles from '../styles/settingsStyles';
import { tryUpdateAppSettingsV2 } from '../../actions/appSettings';
import {
    displayTextForAppPlatform,
    displayTextForUserGroupSingular,
    displayUserNameLong,
    displayUserNameNoEmail,
} from '../../helpers/displayHelpers';
import { PlusOutlined } from '@ant-design/icons';
import { antGrey } from '../styles/globalStyles';
import { Link, useParams } from 'react-router-dom';
import { createOrgSettingsRoute } from '../../helpers/routeHelpers';
import Space from 'antd/lib/space';
import Popconfirm from 'antd/lib/popconfirm';
import message from 'antd/lib/message';
import { globalMessage } from '../../utils/globalMessage';
import Modal from 'antd/lib/modal';
import Form from 'antd/lib/form';
import Select from 'antd/lib/select';
import { selectAppOrgUsers } from '@/selectors/memoizedSelectors';
import { keyBy } from 'lodash';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

interface AddUsersModalProps {
    open: boolean;
    appId: string;
    orgId: string;
    onCancel: () => void;
}

const AddUsersModal: React.FC<AddUsersModalProps> = (props: AddUsersModalProps) => {
    const [form] = Form.useForm();
    const dispatch = useDispatch();

    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));

    const appPlatform = useSelector((state: RootState) => selectAppPlatform(state, props.appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, props.appId));

    const formUserIds = Form.useWatch('userIds', form);

    const orgUsers = useSelector((state: RootState) => selectOrgUsers(state, props.orgId));
    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, props.appId));

    const appUsersById = React.useMemo(() => {
        return keyBy(appUsers, (appUser) => appUser.id);
    }, [appUsers]);

    const addAppUsersIsLoading = useIsLoading(AppSettingsV2Updatable.AddAppUsers);
    const addAppUsersError = useError(AppSettingsV2Updatable.AddAppUsers);
    const addAppUsersSuccess = useUpdated(AppSettingsV2Updatable.AddAppUsers);

    const addAppUsers = (values) => {
        dispatch(
            tryUpdateAppSettingsV2(AppSettingsV2Updatable.AddAppUsers, {
                orgId: props.orgId,
                appId: props.appId,
                userIds: values.userIds,
            }),
        );
    };

    React.useEffect(() => {
        if (addAppUsersError) {
            message.error(addAppUsersError);
        }
    }, [addAppUsersError]);

    React.useEffect(() => {
        if (addAppUsersSuccess) {
            message.success('App users list updated!');

            props.onCancel();
        }
    }, [addAppUsersSuccess]);

    return (
        <Modal
            title={`Add users to ${appName} (${displayTextForAppPlatform(appPlatform, appStoreType)})`}
            okButtonProps={{ loading: addAppUsersIsLoading, disabled: formUserIds?.length === 0 }}
            onOk={() => form.submit()}
            okText="Add users"
            open={props.open}
            onCancel={props.onCancel}
            destroyOnClose
        >
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <Text style={{ color: antGrey.grey7, marginBottom: 16 }}>
                    Select users from this organization to add to {appName} (
                    {displayTextForAppPlatform(appPlatform, appStoreType)})
                </Text>
                <Form onFinish={addAppUsers} initialValues={{ users: [] }} form={form} preserve={false}>
                    <Form.Item name="userIds" rules={[{ required: true, message: 'Please select at least one user' }]}>
                        <Select mode="multiple" placeholder="Select users">
                            {orgUsers.map((user) => (
                                <Select.Option key={user.id} value={user.id} disabled={appUsersById[user.id]}>
                                    {displayUserNameLong(user)}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                </Form>
            </div>
        </Modal>
    );
};

interface RemoveUserActionProps {
    userId: string;
    orgId: string;
    appName: string;
    appId: string;
}

const RemoveUserAction: React.FC<RemoveUserActionProps> = (props: RemoveUserActionProps) => {
    const dispatch = useDispatch();

    const itemDeleteIsLoading = useIsLoading(AppSettingsV2Updatable.RemoveAppUsers, props.userId);
    const itemDeleteError = useError(AppSettingsV2Updatable.RemoveAppUsers, props.userId);
    const itemDeleteSuccess = useUpdated(AppSettingsV2Updatable.RemoveAppUsers, props.userId);

    const [isPopoverVisible, setIsPopoverVisible] = React.useState(false);

    React.useEffect(() => {
        if (itemDeleteError) {
            globalMessage.error(itemDeleteError);
        }
    }, [itemDeleteError]);

    React.useEffect(() => {
        if (itemDeleteSuccess) {
            globalMessage.success('App users list updated!');
            setIsPopoverVisible(false);
        }
    }, [itemDeleteSuccess]);

    const removeUser = (userId: string) => {
        dispatch(
            tryUpdateAppSettingsV2(
                AppSettingsV2Updatable.RemoveAppUsers,
                {
                    userIds: [userId],
                    appId: props.appId,
                    orgId: props.orgId,
                },
                userId,
            ),
        );
    };

    return (
        <Popconfirm
            open={isPopoverVisible}
            title={`Are you sure you want to remove this user from ${props.appName}?`}
            okText="Yes, remove."
            okButtonProps={{ loading: itemDeleteIsLoading, danger: true }}
            onConfirm={() => removeUser(props.userId)}
            onCancel={() => setIsPopoverVisible(false)}
            overlayStyle={{ maxWidth: 400 }}
            placement="topRight"
        >
            <PermissionedActionButton
                actionType={RunwayUserAction.UAUpdateAppSettings}
                type="link"
                onClick={() => {
                    setIsPopoverVisible(!isPopoverVisible);
                }}
                danger
            >
                Remove
            </PermissionedActionButton>
        </Popconfirm>
    );
};
export const AppUsersTable: React.FC<AppComponentProps> = (props: AppComponentProps) => {
    const { orgIdParam } = useParams<AppRouteComponentMatchParams>();

    const [isAddUsersModalVisible, setIsAddUsersModalVisible] = React.useState(false);

    const appUsers = useSelector((state: RootState) => selectAppOrgUsers(state, props.appId));
    const appName = useSelector((state: RootState) => selectAppName(state, props.appId));
    const allGroups = useSelector((state: RootState) => selectOrgGroups(state, props.orgId));

    const renderTitle = (title: string) => {
        return <Text strong>{title}</Text>;
    };

    const renderActions = (user: OrgUser) => {
        return <RemoveUserAction userId={user.id} appId={props.appId} orgId={props.orgId} appName={appName} />;
    };

    const columns: ColumnType<OrgUser>[] = [
        {
            title: renderTitle('Name'),
            render: (value: OrgUser) => {
                const userName = displayUserNameNoEmail(value);
                if (!userName) {
                    return <Text type="secondary">No name</Text>;
                }
                return <Text key={value.id}>{userName}</Text>;
            },
        },
        {
            title: renderTitle('Email'),
            dataIndex: 'email',
            key: 'email',
        },
        {
            title: renderTitle('Groups'),
            render: (userGroups: UserGroupId[]) => {
                if (!userGroups || userGroups?.length === 0) {
                    return <Text type="secondary">No groups</Text>;
                }

                const userGroupsText = userGroups
                    .map((groupId) => displayTextForUserGroupSingular(allGroups[groupId]))
                    .join(', ');

                return <Text>{userGroupsText}</Text>;
            },
            key: 'groups',
            dataIndex: 'groups',
        },
        {
            title: '',
            align: 'right',
            render: (user: OrgUser, _) => {
                return renderActions(user);
            },
        },
    ];

    const orgSettingsRoute = createOrgSettingsRoute(orgIdParam, OrgSettingsTabType.Team);

    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', flexGrow: 1, alignItems: 'center' }}>
                <Space direction="vertical" size={8}>
                    <h4 style={styles.appSettingsSectionTitleStyle}>App users</h4>
                    <Text type="secondary">
                        You can update user groups in your{' '}
                        <Link to={orgSettingsRoute}>Organization&apos;s Team Settings</Link>.
                    </Text>
                </Space>
                <PermissionedActionButton
                    actionType={RunwayUserAction.UAUpdateAppSettings}
                    onClick={() => setIsAddUsersModalVisible(true)}
                >
                    <PlusOutlined />
                    Add app users
                </PermissionedActionButton>
            </div>
            <Table
                dataSource={appUsers.map((user) => {
                    return { ...user, key: user.id };
                })}
                columns={columns}
                pagination={{ hideOnSinglePage: true, defaultPageSize: 5 }}
            />
            {isAddUsersModalVisible && (
                <AddUsersModal
                    open={isAddUsersModalVisible}
                    appId={props.appId}
                    orgId={props.orgId}
                    onCancel={() => setIsAddUsersModalVisible(false)}
                />
            )}
        </Space>
    );
};
